import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowUpRight, 
  Mail, 
  Clock, 
  Instagram,
  Youtube,
  Facebook,
  Linkedin,
  Twitter,
  Heart,
  ArrowUp,
  Sparkles,
  Send,
  MessageCircle
} from 'lucide-react';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubscribed(true);
      setTimeout(() => {
        setIsSubscribed(false);
        setEmail('');
      }, 3000);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = {
    servicos: [
      { label: 'Abrir um CNPJ MEI', url: '/como-abrir-mei' },
      { label: 'Emitir Nota Fiscal', url: '/nota-fiscal-mei' },
      { label: 'Gerar DAS MEI', url: '/das-mei-2025' },
      { label: 'Declaração Anual MEI', url: '/declaracao-anual-mei' },
      { label: 'Ver Planos', url: '/planos' },
    ],
    conteudo: [
      { label: 'Blog', url: '/blog' },
      { label: 'O que é MEI?', url: '/o-que-e-mei' },
      { label: 'Limite de Faturamento', url: '/limite-faturamento-mei' },
      { label: 'MEI e INSS', url: '/mei-inss' },
      { label: 'Perguntas Frequentes', url: '/faq' },
    ],
    institucional: [
      { label: 'Quem Somos', url: '/quem-somos' },
      { label: 'Na Mídia', url: '/na-midia' },
      { label: 'Contato', url: '/contato' },
      { label: 'Política de Privacidade', url: '/politica-de-privacidade' },
    ],
  };

  const socialLinks = [
    { icon: Instagram, url: 'https://instagram.com/meumei.net', color: '#E4405F', label: 'Instagram' },
    { icon: Youtube, url: 'https://youtube.com/@meumei', color: '#FF0000', label: 'YouTube' },
    { icon: Facebook, url: 'https://facebook.com/meumei.net', color: '#1877F2', label: 'Facebook' },
    { icon: Linkedin, url: 'https://linkedin.com/company/meumei', color: '#0A66C2', label: 'LinkedIn' },
    { icon: MessageCircle, url: 'https://wa.me/5511939614816', color: '#25D366', label: 'WhatsApp' },
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Ondas animadas no topo */}
      <div className="relative h-32 md:h-40 overflow-hidden bg-white">
        {/* Onda 1 - Mais clara, atrás */}
        <svg
          className="absolute bottom-0 w-full"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          style={{ height: '100%' }}
        >
          <motion.path
            d="M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z"
            fill="rgba(16, 185, 129, 0.1)"
            animate={{
              d: [
                "M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z",
                "M0,80 C240,20 480,100 720,40 C960,0 1200,80 1440,40 L1440,120 L0,120 Z",
                "M0,60 C240,120 480,0 720,60 C960,120 1200,0 1440,60 L1440,120 L0,120 Z",
              ],
            }}
            transition={{
              duration: 8,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </svg>

        {/* Onda 2 - Média */}
        <svg
          className="absolute bottom-0 w-full"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          style={{ height: '90%' }}
        >
          <motion.path
            d="M0,40 C360,100 720,0 1080,60 C1260,90 1380,30 1440,50 L1440,120 L0,120 Z"
            fill="rgba(16, 185, 129, 0.2)"
            animate={{
              d: [
                "M0,40 C360,100 720,0 1080,60 C1260,90 1380,30 1440,50 L1440,120 L0,120 Z",
                "M0,70 C360,10 720,90 1080,30 C1260,10 1380,70 1440,40 L1440,120 L0,120 Z",
                "M0,40 C360,100 720,0 1080,60 C1260,90 1380,30 1440,50 L1440,120 L0,120 Z",
              ],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5,
            }}
          />
        </svg>

        {/* Onda 3 - Principal, na frente */}
        <svg
          className="absolute bottom-0 w-full"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          style={{ height: '80%' }}
        >
          <motion.path
            d="M0,30 C180,90 360,10 540,50 C720,90 900,20 1080,50 C1260,80 1380,40 1440,60 L1440,120 L0,120 Z"
            fill="#10b981"
            animate={{
              d: [
                "M0,30 C180,90 360,10 540,50 C720,90 900,20 1080,50 C1260,80 1380,40 1440,60 L1440,120 L0,120 Z",
                "M0,60 C180,20 360,80 540,40 C720,10 900,70 1080,30 C1260,0 1380,60 1440,30 L1440,120 L0,120 Z",
                "M0,30 C180,90 360,10 540,50 C720,90 900,20 1080,50 C1260,80 1380,40 1440,60 L1440,120 L0,120 Z",
              ],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </svg>

        {/* Partículas flutuantes na área da onda */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-white/30 rounded-full"
            style={{
              left: `${10 + i * 12}%`,
              bottom: '20%',
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.3, 0.8, 0.3],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              delay: i * 0.3,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Conteúdo principal do footer */}
      <div className="bg-[#10b981] relative">
        {/* Padrão de fundo */}
        <div className="absolute inset-0 pointer-events-none opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.3) 1px, transparent 0)`,
              backgroundSize: '30px 30px',
            }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 py-16 relative z-10">
          {/* Seção superior - Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16 pb-16 border-b border-white/20"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 mb-6"
            >
              <Sparkles size={16} className="text-[#d9f99d]" />
              <span className="text-white text-sm font-medium">Fique por dentro das novidades</span>
            </motion.div>

            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Receba dicas exclusivas para MEIs
            </h3>
            <p className="text-white/80 mb-8 max-w-md mx-auto">
              Cadastre-se e receba conteúdos sobre gestão, impostos e como fazer seu negócio crescer.
            </p>

            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Seu melhor e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:border-white/40"
              />
              <motion.button
                type="submit"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-6 py-3 bg-white text-emerald-600 font-bold rounded-xl flex items-center justify-center gap-2"
              >
                {isSubscribed ? (
                  'Inscrito! ✓'
                ) : (
                  <>
                    <Send size={18} />
                    Inscrever
                  </>
                )}
              </motion.button>
            </form>
          </motion.div>

          {/* Grid de Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-8 lg:gap-12">
            {/* Logo e Descrição */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="col-span-2 md:col-span-4 lg:col-span-4"
            >
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://res.cloudinary.com/dlk1ta1fr/image/upload/v1765801569/O2-Oxygen-MeuMei_Logo_b_kyakhc.png" 
                  alt="MeuMei Logo"
                  className="h-20 md:h-24 w-auto"
                />
              </div>
              <p className="text-white/80 mb-6 leading-relaxed">
                A plataforma completa para o MEI brasileiro. Simplificamos sua vida 
                para você focar no que realmente importa: fazer seu negócio crescer.
              </p>
            </motion.div>

            {/* Links - Serviços */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              <h4 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-[#d9f99d] rounded-full" />
                Serviços
              </h4>
              <ul className="space-y-3">
                {footerLinks.servicos.map((link, index) => (
                  <motion.li 
                    key={link.label}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.1 + index * 0.05 }}
                  >
                    <a
                      href={link.url}
                      className="group flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                    >
                      <span className="group-hover:underline">{link.label}</span>
                      <ArrowUpRight 
                        size={14} 
                        className="opacity-0 group-hover:opacity-100 transition-opacity" 
                      />
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Links - Conteúdo/Blog */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="lg:col-span-2"
            >
              <h4 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-[#d9f99d] rounded-full" />
                Aprenda
              </h4>
              <ul className="space-y-3">
                {footerLinks.conteudo.map((link, index) => (
                  <motion.li 
                    key={link.label}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.15 + index * 0.05 }}
                  >
                    <a
                      href={link.url}
                      className="group flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                    >
                      <span className="group-hover:underline">{link.label}</span>
                      <ArrowUpRight 
                        size={14} 
                        className="opacity-0 group-hover:opacity-100 transition-opacity" 
                      />
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Links - Institucional */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-2"
            >
              <h4 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-[#d9f99d] rounded-full" />
                Institucional
              </h4>
              <ul className="space-y-3">
                {footerLinks.institucional.map((link, index) => (
                  <motion.li 
                    key={link.label}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.2 + index * 0.05 }}
                  >
                    <a
                      href={link.url}
                      className="group flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                    >
                      <span className="group-hover:underline">{link.label}</span>
                      <ArrowUpRight 
                        size={14} 
                        className="opacity-0 group-hover:opacity-100 transition-opacity" 
                      />
                    </a>
                  </motion.li>
                ))}
              </ul>
            </motion.div>

            {/* Contato */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="col-span-2 lg:col-span-2"
            >
              <h4 className="text-white font-bold text-lg mb-6 flex items-center gap-2">
                <span className="w-8 h-1 bg-[#d9f99d] rounded-full" />
                Contato
              </h4>
              
              <div className="space-y-4">
                <motion.a
                  href="mailto:contato@meumei.net"
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <Mail size={18} />
                  </div>
                  <span className="text-sm">contato@meumei.net</span>
                </motion.a>

                <motion.a
                  href="https://wa.me/5511939614816"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-3 text-white/80 hover:text-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <MessageCircle size={18} />
                  </div>
                  <span className="text-sm">(11) 93961-4816</span>
                </motion.a>

                <motion.div
                  whileHover={{ x: 5 }}
                  className="flex items-center gap-3 text-white/80"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <Clock size={18} />
                  </div>
                  <div className="text-sm">
                    <p>Seg a Sex</p>
                    <p className="text-white">09:00 às 18:00</p>
                  </div>
                </motion.div>

                {/* Redes sociais */}
                <div className="pt-4">
                  <p className="text-white/60 text-sm mb-3">Siga-nos</p>
                  <div className="flex gap-2">
                    {socialLinks.map((social, index) => (
                      <motion.a
                        key={social.label}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.4 + index * 0.1 }}
                        whileHover={{ 
                          scale: 1.2,
                          backgroundColor: social.color,
                        }}
                        className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white transition-colors"
                        title={social.label}
                      >
                        <social.icon size={16} />
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Linha divisória animada */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            className="h-px bg-gradient-to-r from-transparent via-white/30 to-transparent my-8"
          />

          {/* Rodapé inferior */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-white/60 text-sm text-center md:text-left"
            >
              © {new Date().getFullYear()} MeuMei - O2 Oxygen Group. Todos os direitos reservados.
            </motion.p>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-white/60 text-sm flex items-center gap-1"
            >
              Feito com{' '}
              <motion.span
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <Heart size={14} className="text-red-400 fill-red-400" />
              </motion.span>
              {' '}para MEIs do Brasil
            </motion.p>

            {/* Botão voltar ao topo */}
            <motion.button
              onClick={scrollToTop}
              whileHover={{ scale: 1.1, y: -3 }}
              whileTap={{ scale: 0.9 }}
              className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <ArrowUp size={20} />
            </motion.button>
          </div>
        </div>

        {/* Decoração de canto */}
        <div className="absolute bottom-0 right-0 w-64 h-64 pointer-events-none opacity-10">
          <svg viewBox="0 0 200 200" className="w-full h-full">
            <circle cx="150" cy="150" r="100" fill="white" />
            <circle cx="150" cy="150" r="70" fill="transparent" stroke="white" strokeWidth="2" />
            <circle cx="150" cy="150" r="40" fill="transparent" stroke="white" strokeWidth="2" />
          </svg>
        </div>
      </div>
    </footer>
  );
};

export default Footer;